import { useState } from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { PERIOD_OPTIONS, type ExitClicksPeriod } from './exitClicksModel';
import { ExitClicksBand } from './ExitClicksBand';
import { useExitClicks } from './useExitClicks';

/**
 * Exit clicks - the destination-side mirror of Store performance and the final
 * reports-suite funnel surface (Reports > Exit clicks, /exit-clicks).
 *
 * Where Store performance asks how well a store converts its own clicks, this
 * asks where outbound traffic is being wasted. It leads on LEAKAGE: matured exits
 * that did not convert, ranked worst-first by leak rate, with the absolute leak
 * volume carried beside it so magnitude reads at a glance. One full-width band
 * renders per market the scope derives to (UK one band, US one band, GLOBAL two
 * stacked), keyed off the region scope, not the switcher.
 *
 * It opens on LAST 30 DAYS ROLLING, the same maturation-lag reasoning as Store
 * performance: a funnel with a maturation lag opens emptiest on a to-date window,
 * so rolling 30 gives a full mostly-matured window every time (D-exit-clicks).
 *
 * Read-only: no provider mutations, so the audit floor stays dormant. All counts
 * come through the exit-clicks seam; the surface never touches a raw click.
 */

/** The surface without the access wrapper. Exported so tests can inject `asOf`. */
export const ExitClicksView = ({ asOf }: { asOf?: number }) => {
  // One reference clock for the period window and the maturation cutoff, captured
  // once via a lazy initializer (so the render body stays pure). Tests inject a
  // fixed value for determinism.
  const [now] = useState<number>(() => asOf ?? Date.now());
  const [period, setPeriod] = useState<ExitClicksPeriod>('last30');

  const { view, isLoading } = useExitClicks({ period, now });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Reports
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Exit clicks
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Where outbound traffic is being wasted. A leak is a matured exit that
        did not convert, so a fresh store with recent clicks never reads as a
        leak.
      </Typography>

      {/* The period governs the total-exits volume and the chart; the leak count
          always applies the maturation cutoff on top of the selected window. */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <TextField
          select
          size="small"
          label="Period"
          value={period}
          onChange={(e) => setPeriod(e.target.value as ExitClicksPeriod)}
          sx={{ minWidth: 220 }}
        >
          {PERIOD_OPTIONS.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {isLoading || !view ? (
        <Loading />
      ) : view.bands.length === 0 ? (
        <Typography color="text.secondary">
          No market is in scope for this account.
        </Typography>
      ) : (
        view.bands.map((band) => (
          <ExitClicksBand key={band.market} band={band} />
        ))
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /exit-clicks. `exitClicks:read` is a read capability,
 * so `viewer` (read everywhere) and `super_admin` see it; the descriptive
 * `editor` and the placeholder support / fraud roles do not, the same
 * reports-suite trust line as Earnings and Store performance (D-exit-clicks).
 */
export const ExitClicks = () => (
  <RequireAccess capability="exitClicks:read">
    <ExitClicksView />
  </RequireAccess>
);
